import { useState, useEffect } from 'react'
import testimonials from '../data/testimonials.json'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:6200'

export default function LoginPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0)

  // Rotate testimonials every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prevIndex) => (prevIndex + 1) % testimonials.length)
    }, 10000)

    return () => clearInterval(interval)
  }, [])

  const currentTestimonial = testimonials[currentTestimonialIndex]

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setMessage('')
    setError('')

    try {
      const response = await fetch(`${API_URL}/api/v1/auth/magic-link/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send magic link')
      }

      setMessage(data.message)
      setEmail('')
    } catch (err) {
      setError(err.message || 'Failed to send magic link. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row w-full">
      {/* Left Panel: Form Section */}
      <div className="lg:w-1/3 flex flex-col justify-between px-6 py-12 lg:px-12 xl:px-16 bg-white transition-colors duration-200">
        <div className="w-full max-w-[480px] mx-auto flex flex-col gap-6">
          {/* Branding / Logo (Mobile only) */}
          <div className="flex lg:hidden mb-4 items-center gap-2">
            <div className="w-8 h-8 rounded bg-primary flex items-center justify-center text-white">
              <span className="material-symbols-outlined">menu_book</span>
            </div>
            <span className="text-xl font-bold tracking-tight">Twinbooks</span>
          </div>

          {/* Page Heading */}
          <div className="flex flex-col gap-2">
            <h1 className="text-[#111418] text-3xl lg:text-4xl font-black leading-tight tracking-[-0.033em]">
              Welcome to Twinbooks
            </h1>
            <p className="text-[#617589] text-base font-normal leading-normal">
              Seamlessly transfer your accounting data.
            </p>
          </div>

          {/* Info Badge */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-primary text-[20px] mt-0.5">info</span>
              <div>
                <p className="text-sm font-medium text-[#111418]">
                  No account yet? No problem!
                </p>
                <p className="text-sm text-[#617589] mt-1">
                  Simply enter your email below. We'll create your account and send you a secure login link.
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="relative flex items-center py-2">
            <div className="flex-grow border-t border-[#dbe0e6]"></div>
            <span className="flex-shrink-0 mx-4 text-sm text-[#617589]">Enter your email</span>
            <div className="flex-grow border-t border-[#dbe0e6]"></div>
          </div>

          {/* Input Fields */}
          <form className="flex flex-col gap-5" onSubmit={handleSubmit} autoComplete="on">
            {/* Email Field */}
            <label className="flex flex-col w-full" htmlFor="email">
              <p className="text-[#111418] text-sm font-semibold leading-normal pb-2">Email Address</p>
              <input
                id="email"
                name="email"
                autoComplete="username"
                className="form-input flex w-full resize-none overflow-hidden rounded-lg text-[#111418] border border-[#dbe0e6] bg-white h-12 px-4 placeholder:text-[#617589] text-base font-normal leading-normal focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-shadow"
                placeholder="name@company.com"
                required
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </label>
            {/* Hidden password field to help password managers recognize this as a login form */}
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              aria-hidden="true"
              tabIndex={-1}
              className="absolute opacity-0 pointer-events-none h-0 w-0"
            />

            {error && (
              <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                <div className="flex items-start">
                  <span className="material-symbols-outlined text-red-600 mr-3 text-[20px]">error</span>
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              </div>
            )}

            {message && (
              <div className="rounded-lg bg-green-50 p-4 border border-green-200">
                <div className="flex items-start">
                  <span className="material-symbols-outlined text-green-600 mr-3 text-[20px]">check_circle</span>
                  <div>
                    <p className="text-sm font-medium text-green-800">{message}</p>
                    <p className="mt-1 text-sm text-green-700">
                      Check your inbox and click the link to sign in.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-bold rounded-lg h-12 mt-2 shadow-sm transition-all focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Sending Magic Link...' : 'Send Magic Link'}
            </button>
          </form>

          {/* Footer Security Badge */}
          <div className="flex items-center justify-center gap-2 mt-4 text-[#617589]">
            <span className="material-symbols-outlined text-[18px]">lock</span>
            <span className="text-xs font-medium">256-bit SSL Secure Connection</span>
          </div>
        </div>

        {/* Footer Links */}
        <div className="w-full max-w-[480px] mx-auto mt-8">
          <div className="flex items-center justify-center gap-6 text-sm text-[#617589] py-4 border-t border-[#dbe0e6]">
            <a href="https://twinbooks.be/contact" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              Contact us
            </a>
            <a href="https://twinbooks.be/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              Privacy
            </a>
            <a href="https://twinbooks.be/terms" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">
              Terms
            </a>
          </div>
        </div>
      </div>

      {/* Right Panel: Visual / Branding */}
      <div className="hidden lg:flex lg:w-2/3 relative bg-primary overflow-hidden flex-col justify-between p-12 xl:p-20 text-white">
        {/* Abstract Background */}
        <div className="absolute inset-0 z-0 opacity-20">
          <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/80 to-primary/40 mix-blend-multiply"></div>
        </div>

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center border border-white/30">
            <span className="material-symbols-outlined text-white text-2xl">menu_book</span>
          </div>
          <span className="text-2xl font-bold tracking-tight">Twinbooks</span>
        </div>

        {/* Illustration / Central Visual */}
        <div className="relative z-10 flex-1 flex items-center justify-center my-10">
          <div className="relative w-full max-w-md aspect-[4/3]">
            {/* Back Card */}
            <div className="absolute top-0 right-0 w-3/4 h-3/4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 transform rotate-6 translate-x-4 translate-y-4 shadow-2xl"></div>

            {/* Front Card */}
            <div className="absolute inset-0 w-full h-full bg-white/10 backdrop-blur-xl rounded-xl border border-white/20 shadow-2xl p-6 flex flex-col justify-between">
              <div className="flex justify-between items-center mb-4">
                <div className="h-3 w-1/3 bg-white/40 rounded-full"></div>
                <div className="h-8 w-8 bg-green-400/80 rounded-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-white text-sm">check</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="h-2 w-full bg-white/20 rounded-full"></div>
                <div className="h-2 w-5/6 bg-white/20 rounded-full"></div>
                <div className="h-2 w-4/6 bg-white/20 rounded-full"></div>
              </div>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-white/20 flex items-center justify-center">
                    <span className="material-symbols-outlined text-white">cloud_sync</span>
                  </div>
                  <div>
                    <div className="h-3 w-24 bg-white/50 rounded-full mb-1"></div>
                    <div className="h-2 w-16 bg-white/30 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating Element */}
            <div className="absolute -left-8 bottom-12 bg-white text-primary px-4 py-3 rounded-lg shadow-xl flex items-center gap-3 animate-pulse">
              <span className="material-symbols-outlined text-green-500">verified_user</span>
              <span className="font-bold text-sm">Transfer Complete</span>
            </div>
          </div>
        </div>

        {/* Testimonial / Trust */}
        <div className="relative z-10">
          <div className="flex mb-4 gap-1">
            <span className="material-symbols-outlined text-yellow-400" style={{fontVariationSettings: "'FILL' 1"}}>star</span>
            <span className="material-symbols-outlined text-yellow-400" style={{fontVariationSettings: "'FILL' 1"}}>star</span>
            <span className="material-symbols-outlined text-yellow-400" style={{fontVariationSettings: "'FILL' 1"}}>star</span>
            <span className="material-symbols-outlined text-yellow-400" style={{fontVariationSettings: "'FILL' 1"}}>star</span>
            <span className="material-symbols-outlined text-yellow-400" style={{fontVariationSettings: "'FILL' 1"}}>star</span>
          </div>
          <div className="relative min-h-[200px]">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`absolute inset-0 transition-opacity duration-1000 ${
                  index === currentTestimonialIndex ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <h2 className="text-2xl lg:text-3xl font-semibold leading-snug mb-2">
                  "{testimonial.quote}"
                </h2>
                <p className="text-white/80 text-lg font-medium">— {testimonial.author}</p>
                <p className="text-white/60 text-sm mt-1">{testimonial.position}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
